import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';
import { IconButton, Grid, ArrowForwardIosOutlinedIcon, ArrowBackIosOutlinedIcon, ClearHighlightIcon, Box } from '@/ui';

import type { TypeContainerBox } from '@/core/types/global-types';
import {
  useDetailsStoreActions,
  useDetailsCheckedFeatures,
  useDetailsLayerDataArrayBatch,
  useDetailsSelectedLayerPath,
  useDetailsCoordinateInfoEnabled,
  useMapHideCoordinateInfoSwitch,
} from '@/core/stores/store-interface-and-intial-values/feature-info-state';
import { useUIActiveAppBarTab, useUIActiveFooterBarTab, useUIStoreActions } from '@/core/stores/store-interface-and-intial-values/ui-state';
import { useLayerNames, useLayerStatuses } from '@/core/stores/store-interface-and-intial-values/layer-state';
import { useGeoViewMapId } from '@/core/stores/geoview-store';
import {
  useMapStoreActions,
  useMapClickCoordinates,
  useMapAllVisibleandInRangeLayers,
  useMapOrderedLayers,
  useMapSelectorLayerQueryable,
} from '@/core/stores/store-interface-and-intial-values/map-state';
import type { TypeFeatureInfoEntry, TypeLayerData } from '@/api/types/map-schema-types';
import type { TypeMapMouseInfo } from '@/geo/map/map-viewer';

import type { LayerListEntry, LayoutExposedMethods } from '@/core/components/common';
import { Layout } from '@/core/components/common';
import { checkSelectedLayerPathList } from '@/core/components/common/comp-common';
import { getSxClasses } from './details-style';
import { FeatureInfo } from './feature-info';
import { CONTAINER_TYPE, FEATURE_INFO_STATUS, TABS, TIMEOUT } from '@/core/utils/constant';
import { DetailsSkeleton } from './details-skeleton';
import { CoordinateInfo, CoordinateInfoSwitch } from './coordinate-info';
import { logger } from '@/core/utils/logger';

/** Properties for the details panel component. */
interface DetailsPanelType {
  /** The container type (appBar or footerBar). */
  containerType: TypeContainerBox;
}

/**
 * Creates the details panel component.
 *
 * @param props - Properties defined in DetailsPanelType interface
 * @returns The details panel component
 */
export function DetailsPanel({ containerType }: DetailsPanelType): JSX.Element {
  logger.logTraceRender('components/details/details-panel');

  // Hooks
  const { t } = useTranslation<string>();
  const theme = useTheme();
  const sxClasses = useMemo(() => getSxClasses(theme), [theme]);

  // Store
  const mapId = useGeoViewMapId();
  const selectedLayerPath = useDetailsSelectedLayerPath();
  const arrayOfLayerDataBatch = useDetailsLayerDataArrayBatch();
  const checkedFeatures = useDetailsCheckedFeatures();
  const coordinateInfoEnabled = useDetailsCoordinateInfoEnabled();
  const hideCoordinateInfoSwitch = useMapHideCoordinateInfoSwitch();
  const visibleInRangeLayers = useMapAllVisibleandInRangeLayers();
  const orderedLayers = useMapOrderedLayers();
  const mapClickCoordinates = useMapClickCoordinates();
  const activeAppBarTab = useUIActiveAppBarTab();
  const activeFooterBarTab = useUIActiveFooterBarTab();
  const queryableByLayerPath = useMapSelectorLayerQueryable(visibleInRangeLayers);
  const { setSelectedLayerPath, removeCheckedFeature, setLayerDataArrayBatchLayerPathBypass } = useDetailsStoreActions();
  const { addHighlightedFeature, removeHighlightedFeature, isLayerHiddenOnMap, getMapLayerParentHidden } = useMapStoreActions();
  const { disableFocusTrap } = useUIStoreActions();
  const layerNames = useLayerNames();
  const layerStatuses = useLayerStatuses();

  // States
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState<number>(0);
  const [selectedLayerPathLocal, setSelectedLayerPathLocal] = useState<string>(selectedLayerPath);
  const [arrayOfLayerListLocal, setArrayOfLayerListLocal] = useState<LayerListEntry[]>([]);
  const [isRightPanelVisible, setIsRightPanelVisible] = useState<boolean>(false);
  const prevLayerSelected = useRef<TypeLayerData>();
  const prevLayerFeatures = useRef<TypeFeatureInfoEntry[] | undefined | null>();
  const prevFeatureIndex = useRef<number>(0); // 0 because that's the default index for the features
  const prevMapClickCoordinates = useRef<TypeMapMouseInfo | undefined>(mapClickCoordinates);
  const layoutRef = useRef<LayoutExposedMethods>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  // #region MAIN HOOKS SECTION

  /**
   * Memoizes the set of checked feature IDs for O(1) lookup.
   */
  // Create a memoized Set of checked feature IDs
  const memoIsCheckedFeaturesSet = useMemo(() => {
    return new Set(checkedFeatures.map((feature) => feature?.uid));
  }, [checkedFeatures]);

  /**
   * Checks if a feature is in the store checkedFeatures array.
   *
   * Modified memoIsCheckedFeaturesSet using the Set for O(1) lookup
   *
   * @param feature - The feature to check
   * @returns Whether the feature is in checkedFeatures
   */
  const isFeatureInCheckedFeatures = useCallback(
    (feature: TypeFeatureInfoEntry): boolean => {
      return memoIsCheckedFeaturesSet.has(feature?.uid);
    },
    [memoIsCheckedFeaturesSet]
  );

  /**
   * Clears the highlighted features when they are not checked.
   *
   * @param arrayToClear - The array to clear of unchecked features
   */
  // Modified clearHighlightsUnchecked
  const clearHighlightsUnchecked = useCallback(
    (arrayToClear: TypeFeatureInfoEntry[] | undefined | null) => {
      arrayToClear?.forEach((feature) => {
        if (!memoIsCheckedFeaturesSet.has(feature.uid)) {
          removeHighlightedFeature(feature);
        }
      });
    },
    [memoIsCheckedFeaturesSet, removeHighlightedFeature]
  );

  /**
   * Gets the label for the number of features of a layer.
   *
   * @param layer - The layer data to get the label for
   * @returns The features count label string
   */
  const getNumFeaturesLabel = useCallback(
    (layer: TypeLayerData): string => {
      const numOfFeatures = layer.features?.length ?? 0;
      const label =
        numOfFeatures === 0
          ? `${t('general.none')} ${t('details.feature')} ${t('details.selected')}`
          : `${numOfFeatures} ${t('details.feature')}${numOfFeatures > 1 ? 's' : ''}`;
      return label;
    },
    [t]
  );

  /**
   * Checks if a feature has valid geometry.
   *
   * @param feature - The feature to check
   * @returns Whether the feature has valid geometry
   */
  const hasValidGeometry = useCallback((feature: TypeFeatureInfoEntry | undefined): boolean => {
    return !!(feature?.geometry && feature?.extent && !feature.extent.includes(Infinity));
  }, []);

  /**
   * Memoizes whether the panel is currently open.
   */
  const memoIsPanelOpen = useMemo(() => {
    if (containerType === CONTAINER_TYPE.FOOTER_BAR) {
      return activeFooterBarTab.tabId === TABS.DETAILS && activeFooterBarTab.isOpen && isRightPanelVisible;
    }
    if (containerType === CONTAINER_TYPE.APP_BAR) {
      return activeAppBarTab.tabId === 'details' && activeAppBarTab.isOpen && isRightPanelVisible;
    }
    return false;
  }, [containerType, activeFooterBarTab, activeAppBarTab, isRightPanelVisible]);

  /**
   * Memoizes the layers list for the LayerList component and centralizing indexing purposes.
   */
  const memoLayersList = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DETAILS-PANEL - memoLayersList', visibleInRangeLayers, arrayOfLayerDataBatch);

    // Set the layers list (filter: visible - visible in range and isQueryable)
    const layerListEntries = visibleInRangeLayers
      .map((layerPath) => arrayOfLayerDataBatch.find((layerData) => layerData.layerPath === layerPath))
      .filter((layer) => layer && !isLayerHiddenOnMap(layer.layerPath))
      .filter((layer) => layer && queryableByLayerPath[layer.layerPath])
      .map(
        (layer) =>
          ({
            layerName: layerNames[layer!.layerPath] ?? '',
            layerPath: layer!.layerPath,
            layerStatus: layerStatuses[layer!.layerPath],
            queryStatus: layer!.queryStatus,
            numOffeatures: layer!.features?.length ?? 0,
            layerFeatures: getNumFeaturesLabel(layer!),
            tooltip: t('layers.selectLayer', { layerName: layerNames[layer!.layerPath] }) ?? '',
            layerUniqueId: `${mapId}-${TABS.DETAILS}-${layer!.layerPath ?? ''}`,
          }) as LayerListEntry
      );

    // Merge in-range and out-of-range layers while preserving order from arrayOfLayerDataBatch
    const existingLayerPaths = new Set(layerListEntries.map((entry) => entry.layerPath));

    // Add layers with features that aren't already in the list (out-of-range layers with features)
    arrayOfLayerDataBatch.forEach((layer) => {
      if (
        (layer.features?.length ?? 0) > 0 &&
        !existingLayerPaths.has(layer.layerPath) &&
        layer.layerPath !== 'coordinate-info' &&
        !getMapLayerParentHidden(layer.layerPath)
      ) {
        layerListEntries.push({
          layerName: layerNames[layer.layerPath] ?? '',
          layerPath: layer.layerPath,
          layerStatus: layerStatuses[layer.layerPath],
          queryStatus: layer.queryStatus,
          numOffeatures: layer.features?.length ?? 0,
          layerFeatures: getNumFeaturesLabel(layer),
          tooltip: t('layers.selectLayer', { layerName: layerNames[layer.layerPath] }) ?? '',
          layerUniqueId: `${mapId}-${TABS.DETAILS}-${layer.layerPath}`,
        });
      }
    });

    // Split the layers list into two groups while preserving order (exclude coordinate-info from sorting)
    const layersWithFeatures = layerListEntries.filter(
      (layer) => layer.numOffeatures && layer.numOffeatures > 0 && layer.layerPath !== 'coordinate-info'
    );
    const layersWithoutFeatures = layerListEntries.filter((layer) => layer.numOffeatures === 0 && layer.layerPath !== 'coordinate-info');

    // Sort layersWithFeatures according to orderedLayers
    layersWithFeatures.sort((a, b) => {
      const indexA = orderedLayers.indexOf(a.layerPath);
      const indexB = orderedLayers.indexOf(b.layerPath);
      // If not found in orderedLayers, put at the end
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    // Combine the lists (features first, then no features)
    const orderedLayerListEntries = [...layersWithFeatures, ...layersWithoutFeatures];

    // Add coordinate info layer at the beginning if it exists and is enabled
    const coordinateInfoLayer = arrayOfLayerDataBatch.find((layer) => layer.layerPath === 'coordinate-info');
    if (coordinateInfoLayer && coordinateInfoEnabled) {
      orderedLayerListEntries.unshift({
        layerName: layerNames[coordinateInfoLayer.layerPath] ?? 'Coordinate Information',
        layerPath: coordinateInfoLayer.layerPath,
        layerStatus: layerStatuses[coordinateInfoLayer.layerPath],
        queryStatus: coordinateInfoLayer.queryStatus,
        numOffeatures: coordinateInfoLayer.features?.length ?? 0,
        layerFeatures: getNumFeaturesLabel(coordinateInfoLayer),
        tooltip: t('layers.selectLayer', { layerName: layerNames[coordinateInfoLayer.layerPath] ?? '' }) ?? '',
        layerUniqueId: `${mapId}-${TABS.DETAILS}-${coordinateInfoLayer.layerPath}`,
      });
    }

    return orderedLayerListEntries;
  }, [
    visibleInRangeLayers,
    arrayOfLayerDataBatch,
    coordinateInfoEnabled,
    isLayerHiddenOnMap,
    queryableByLayerPath,
    layerNames,
    layerStatuses,
    getNumFeaturesLabel,
    mapId,
    getMapLayerParentHidden,
    orderedLayers,
    t,
  ]);

  /**
   * Memoizes the selected layer for the LayerList component.
   */
  const memoLayerSelectedItem = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DETAILS-PANEL - memoLayerSelectedItem', memoLayersList, selectedLayerPath);

    return memoLayersList.find((layer) => layer.layerPath === selectedLayerPath);
  }, [memoLayersList, selectedLayerPath]);

  /**
   * Memoizes the selected layer data.
   */
  const memoSelectedLayerData = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DETAILS-PANEL - memoSelectedLayerData', arrayOfLayerDataBatch, selectedLayerPath);

    return arrayOfLayerDataBatch.find((layer) => layer.layerPath === selectedLayerPath);
  }, [arrayOfLayerDataBatch, selectedLayerPath]);

  /**
   * Memoizes the selected layer data features.
   */
  const memoSelectedLayerDataFeatures = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DETAILS-PANEL - memoSelectedLayerDataFeatures', memoSelectedLayerData?.features);

    return memoSelectedLayerData?.features;
  }, [memoSelectedLayerData?.features]);

  /**
   * Memoizes the current feature.
   */
  const memoCurrentFeature = useMemo(() => {
    // The current feature
    logger.logTraceUseMemo('DETAILS-PANEL - memoCurrentFeature', currentFeatureIndex);

    return memoSelectedLayerDataFeatures?.[currentFeatureIndex];
  }, [memoSelectedLayerDataFeatures, currentFeatureIndex]);

  /**
   * Memoizes the current feature has geometry.
   */
  const memoCurrentFeatureHasGeometry = useMemo(() => {
    // The current feature has its geometry loaded
    logger.logTraceUseMemo('DETAILS-PANEL - memoCurrentFeatureHasGeometry', !!memoCurrentFeature?.geometry);

    return !!memoCurrentFeature?.geometry;
  }, [memoCurrentFeature?.geometry]);

  /**
   * Updates the selected features for the highlight on the map.
   *
   * Removes the previously highlighted feature and adds a new one.
   *
   * @param newIndex - The new index to select the feature
   * @param prevLayer - Optional layer on which to unselect features
   */
  const updateFeatureSelected = useCallback(
    (newIndex: number, prevLayer?: TypeLayerData) => {
      // Get the current feature in highlight
      let currentFeature;
      if (prevLayer?.features) {
        currentFeature = prevLayer?.features?.[prevFeatureIndex.current];
      }

      // If found, remove it
      if (currentFeature && !isFeatureInCheckedFeatures(currentFeature)) removeHighlightedFeature(currentFeature);

      // Get the next feature navigating to
      const nextFeature = memoSelectedLayerData?.features?.[newIndex];

      // If found, add it
      if (nextFeature) addHighlightedFeature(nextFeature);

      // Update the current feature index
      setCurrentFeatureIndex(newIndex);
    },
    [memoSelectedLayerData?.features, isFeatureInCheckedFeatures, removeHighlightedFeature, addHighlightedFeature]
  );

  /**
   * Effect used when the layers list changes.
   * Note: This useEffect is triggered many times as the layerDataArray gets processed.
   * History: Logic was initially in click-marker. Brought here.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - memoLayersList changed', memoLayersList);

    // Unselect the layer path if no more layers in the list
    if (!memoLayersList.length) setSelectedLayerPath('');
  }, [memoLayersList, setSelectedLayerPath]);

  /**
   * Effect used when the layers list changes.
   * Note: This useEffect is triggered many times as the layerDataArray gets processed.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - memoSelectedLayerDataFeatures changed', memoSelectedLayerDataFeatures);

    // Clear the unchecked highlights
    clearHighlightsUnchecked(prevLayerFeatures.current);

    // Re-highlight all checked features to ensure they persist through zoom
    checkedFeatures.forEach((checkedFeature) => {
      if (hasValidGeometry(checkedFeature)) {
        addHighlightedFeature(checkedFeature);
      }
    });
    // TODO: REFACTOR - The details-panel should be refactored to simplify the logic of the highlighted features, it's a bit confusing right now, too many useEffects
    // TO.DOCONT: For example here, it's weird to have the 'memoSelectedLayerDataFeatures' in the dependency array here to make it work...
  }, [memoSelectedLayerDataFeatures, addHighlightedFeature, clearHighlightsUnchecked, checkedFeatures, hasValidGeometry]);

  /**
   * Use Effect for when the current feature has a geometry loaded
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - memoCurrentFeature changed', memoCurrentFeature);

    // If current feature
    if (memoCurrentFeature) {
      // If the geometry has been loaded
      if (memoCurrentFeatureHasGeometry) {
        if (memoIsPanelOpen) addHighlightedFeature(memoCurrentFeature);
      } else {
        removeHighlightedFeature(memoCurrentFeature);
      }
    }
  }, [memoCurrentFeature, memoCurrentFeatureHasGeometry, memoIsPanelOpen, addHighlightedFeature, removeHighlightedFeature]);

  /**
   * Effect used to persist the layer path bypass for the layerDataArray.
   * A useEffect is necessary in order to keep this component pure and be able to set the layer path bypass elsewhere than in this component.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - update layer data bypass', selectedLayerPath);

    // Set the layer data array batch bypass to the currently selected layer
    setLayerDataArrayBatchLayerPathBypass(selectedLayerPath);
  }, [selectedLayerPath, setLayerDataArrayBatchLayerPathBypass]);

  /**
   * Effect used to persist or alter the current layer selection based on the layers list changes
   * A useEffect is necessary in order to keep this component pure and be able to set the selected layer path elsewhere than in this component.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - check selection', memoLayerSelectedItem, selectedLayerPath);

    // If selected layer path is not empty, launch the checker to try to maintain the selection on the correct selected layer
    if (selectedLayerPath) {
      // Redirect to the keep selected layer path logic
      checkSelectedLayerPathList(setLayerDataArrayBatchLayerPathBypass, setSelectedLayerPath, memoLayerSelectedItem, memoLayersList);
    }
  }, [memoLayerSelectedItem, memoLayersList, selectedLayerPath, setLayerDataArrayBatchLayerPathBypass, setSelectedLayerPath]);

  // #endregion

  // #region EVENT HANDLERS SECTION

  /**
   * Handles click to remove all features in right panel.
   */
  const handleClearAllHighlights = (): void => {
    if (checkedFeatures.length === 0) return;
    // clear all highlights from features on the map in all layers
    removeHighlightedFeature('all');
    // clear checked features array
    removeCheckedFeature('all');
    // add the highlight to the current feature
    addHighlightedFeature(memoSelectedLayerData?.features?.[currentFeatureIndex] as TypeFeatureInfoEntry);
  };

  /**
   * Handles when the right panel is closed in responsive layout.
   * Removes the currently selected feature highlight (but keeps checked features).
   */
  const handleRightPanelClosed = useCallback((): void => {
    // Only remove the current selected feature highlight if it's not checked
    const currentFeature = memoSelectedLayerData?.features?.[currentFeatureIndex];
    if (currentFeature && !isFeatureInCheckedFeatures(currentFeature)) {
      removeHighlightedFeature(currentFeature);
    }

    // Return focus to the layer list item that opened this panel
    if (selectedLayerPath) {
      // Construct the layer list item ID
      const layerListItemId = `${mapId}-${TABS.DETAILS}-${selectedLayerPath}`;
      disableFocusTrap(layerListItemId);
    } else {
      // No layer selected, don't move focus
      disableFocusTrap('no-focus');
    }
  }, [
    removeHighlightedFeature,
    memoSelectedLayerData,
    currentFeatureIndex,
    isFeatureInCheckedFeatures,
    selectedLayerPath,
    mapId,
    disableFocusTrap,
  ]);

  /**
   * Handles when the right panel visibility changes.
   */
  const handleRightPanelVisibilityChanged = useCallback((isVisible: boolean): void => {
    setIsRightPanelVisible(isVisible);
  }, []);

  /**
   * Handles clicks to forward and back arrows in right panel.
   * Removes previous feature from selectedFeatures store if it is not checked, and adds new feature.
   *
   * @param change - The change to index number (-1 for back, 1 for forward)
   */
  const handleFeatureNavigateChange = useCallback(
    (change: -1 | 1): void => {
      const newIndex = currentFeatureIndex + change;
      const maxIndex = (memoSelectedLayerData?.features?.length ?? 0) - 1;

      // Don't navigate if out of bounds
      if (newIndex < 0 || newIndex > maxIndex) {
        return;
      }

      // Keep previous index for navigation
      prevFeatureIndex.current = currentFeatureIndex;

      // Update current index
      updateFeatureSelected(newIndex, memoSelectedLayerData);

      // Restore focus to the navigation button after React completes the state update and re-render
      requestAnimationFrame(() => {
        if (change === -1) {
          prevButtonRef.current?.focus();
        } else {
          nextButtonRef.current?.focus();
        }
      });
    },
    [currentFeatureIndex, memoSelectedLayerData, updateFeatureSelected]
  );

  /**
   * Handles click to change the selected layer in left panel.
   *
   * @param layerEntry - The data of the newly selected layer
   */
  const handleLayerChange = useCallback(
    (layerEntry: LayerListEntry): void => {
      // Set the selected layer path in the store which will in turn trigger the store listeners on this component
      setSelectedLayerPath(layerEntry.layerPath);

      // Re-highlight the current feature when panel becomes visible (layer selection makes panel visible)
      // Use setTimeout to ensure the layer data is updated first
      setTimeout(() => {
        const layerData = arrayOfLayerDataBatch.find((layer) => layer.layerPath === layerEntry.layerPath);
        const featureToHighlight = layerData?.features?.[0]; // Will be index 0 after layer change
        if (featureToHighlight && hasValidGeometry(featureToHighlight)) {
          addHighlightedFeature(featureToHighlight);
        }
      }, TIMEOUT.deferExecution);
    },
    [setSelectedLayerPath, arrayOfLayerDataBatch, hasValidGeometry, addHighlightedFeature]
  );
  // #endregion

  // #region PROCESSING

  /**
   * Resets the currently selected feature index to 0 and keeps in reference the previously selected layer and
   * the previously selected feature index so that in the useEffect, later, the component can udpate
   * the selected features with the store.
   */
  const resetCurrentIndex = (): void => {
    // Keep reference on previously selected layer
    prevLayerSelected.current = arrayOfLayerDataBatch.find((layer) => layer.layerPath === selectedLayerPathLocal);
    // Keep reference on previously selected features
    prevLayerFeatures.current = prevLayerSelected.current?.features;
    // Keep reference on previously selected index
    prevFeatureIndex.current = currentFeatureIndex;
    // Reset the indexing
    setCurrentFeatureIndex(0);
  };

  // If the array of layer data has changed since last render
  if (arrayOfLayerListLocal !== memoLayersList) {
    // Selected array layer data changed
    setArrayOfLayerListLocal(memoLayersList);

    // Only reset the feature index if the number of features in the currently selected layer changed
    const currentSelectedLayer = memoLayersList.find((layer) => layer.layerPath === selectedLayerPath);
    const previousSelectedLayer = arrayOfLayerListLocal.find((layer) => layer.layerPath === selectedLayerPath);

    // Reset index only if feature count changed for the selected layer
    if (currentSelectedLayer && previousSelectedLayer && currentSelectedLayer.numOffeatures !== previousSelectedLayer.numOffeatures) {
      resetCurrentIndex();
    }
  }

  // If the layer path has changed since last render
  if (selectedLayerPathLocal !== selectedLayerPath) {
    // Selected layer path changed
    setSelectedLayerPathLocal(selectedLayerPath);
    // Reset the feature index, because it's a whole different selected layer with different features
    resetCurrentIndex();
  }

  /**
   * Selects a layer after a map click.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL- mapClickCoordinates', mapClickCoordinates);

    // Check if coordinates actually changed (new map click)
    const coordinatesChanged =
      mapClickCoordinates && JSON.stringify(mapClickCoordinates) !== JSON.stringify(prevMapClickCoordinates.current);

    // Show the details for a feature on map click
    if (mapClickCoordinates && memoLayersList?.length) {
      // if we don't have a selected layer path with features select the first layer path with features
      if (!selectedLayerPath.length) {
        const selectedLayer = memoLayersList.find((layer) => !!layer.numOffeatures);
        setSelectedLayerPath(selectedLayer?.layerPath ?? '');
        // Ensure the info panel is visible
        layoutRef.current?.showRightPanel(true);
      }

      // Make sure the right panel is visible as long as the coordinates have changed from a user click
      if (!isRightPanelVisible && coordinatesChanged) {
        layoutRef.current?.showRightPanel(true);
      }
    }

    // On new map click (coordinates changed), clear all highlights, checked features, and layer features
    if (coordinatesChanged) {
      removeHighlightedFeature('all');
      removeCheckedFeature('all');
      // Clear features from all layers to remove out-of-range layers from display
      arrayOfLayerDataBatch.forEach((layer) => {
        // eslint-disable-next-line no-param-reassign
        layer.features = [];
      });
      // Update the ref to current coordinates
      prevMapClickCoordinates.current = mapClickCoordinates;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapClickCoordinates, memoLayersList, setSelectedLayerPath, coordinateInfoEnabled]);

  /**
   * Clear highlights and checked features when the details panel is closed
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - panel closed check', activeFooterBarTab, activeAppBarTab, containerType);

    let shouldClear = false;

    if (containerType === CONTAINER_TYPE.FOOTER_BAR) {
      // For footer bar: clear when not on details tab or footer is collapsed
      shouldClear = activeFooterBarTab.tabId !== TABS.DETAILS || !activeFooterBarTab.isOpen;
    } else if (containerType === CONTAINER_TYPE.APP_BAR) {
      // For app bar: clear when details panel is closed (tabId === 'details' and isOpen === false)
      shouldClear = activeAppBarTab.tabId === TABS.DETAILS && !activeAppBarTab.isOpen;
    }

    if (shouldClear) {
      logger.logTraceUseEffect('DETAILS-PANEL - panel closed check !!!!');

      // Clear all highlights
      removeHighlightedFeature('all');
      // Clear all checked features
      removeCheckedFeature('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFooterBarTab, activeAppBarTab, containerType]);

  /**
   * Checks if all layers query status is processed.
   */
  const memoIsAllLayersQueryStatusProcessed = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DETAILS-PANEL - AllLayersQueryStatusProcessed.');

    if (!arrayOfLayerDataBatch || arrayOfLayerDataBatch?.length === 0) return () => false;

    return () => arrayOfLayerDataBatch?.every((layer) => layer.queryStatus === FEATURE_INFO_STATUS.PROCESSED);
  }, [arrayOfLayerDataBatch]);

  /**
   * Automatically show the guide when panel opens and all layers have no features
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DETAILS-PANEL - check for auto-guide display', activeAppBarTab, activeFooterBarTab, arrayOfLayerDataBatch);

    // Check if details panel just opened (from AppBar or Footer)
    const isDetailsActive =
      (containerType === CONTAINER_TYPE.FOOTER_BAR && activeFooterBarTab.tabId === TABS.DETAILS && activeFooterBarTab.isOpen) ||
      (containerType === CONTAINER_TYPE.APP_BAR && activeAppBarTab.tabId === TABS.DETAILS && activeAppBarTab.isOpen);

    // Only run when details is active
    if (isDetailsActive && arrayOfLayerDataBatch && arrayOfLayerDataBatch.length > 0) {
      // Check if all layers have no features (excluding coordinate-info)
      const allLayersHaveNoFeatures = arrayOfLayerDataBatch.every(
        (layer) => layer.layerPath === 'coordinate-info' || !layer.features || layer.features.length === 0
      );

      // If all layers have no features and queries are processed
      if (allLayersHaveNoFeatures && memoIsAllLayersQueryStatusProcessed()) {
        logger.logTraceUseEffect('DETAILS-PANEL - All layers have no features, showing right panel with guide');
        // Clear selection to show the guide
        setSelectedLayerPath('');
        // Make sure the right panel is visible
        if (!isRightPanelVisible) {
          layoutRef.current?.showRightPanel(true);
        }
      }
    }
  }, [
    activeAppBarTab,
    activeFooterBarTab,
    containerType,
    arrayOfLayerDataBatch,
    memoIsAllLayersQueryStatusProcessed,
    setSelectedLayerPath,
    isRightPanelVisible,
  ]);

  // #endregion

  /**
   * Renders the right panel content based on detail's layer and loading status.
   *
   * NOTE: Here we return null, so that in responsive grid layout, it can be used as flag to render the guide for details.
   *
   * @returns The right panel content, or null to show the guide
   */
  const renderContent = (): JSX.Element | null => {
    if (selectedLayerPath === 'coordinate-info') {
      return <CoordinateInfo />;
    }

    // If there is no layer, return null for the guide to show
    if ((memoLayersList && memoLayersList.length === 0) || selectedLayerPath === '') {
      return null;
    }

    // Until process or something found for selected layerPath, return skeleton
    if (!memoIsAllLayersQueryStatusProcessed() && !(memoSelectedLayerDataFeatures && memoSelectedLayerDataFeatures.length > 0)) {
      return <DetailsSkeleton />;
    }

    if (memoCurrentFeature) {
      // Get only the current feature
      const isPrevDisabled = currentFeatureIndex <= 0;
      const isNextDisabled = !memoSelectedLayerData?.features || currentFeatureIndex + 1 >= memoSelectedLayerData.features.length;

      return (
        <Box sx={sxClasses.rightPanelContainer} className="guide-content-container">
          <Grid container sx={sxClasses.rightPanelBtnHolder}>
            <Grid size={{ xs: 6 }} sx={{ alignSelf: 'center' }}>
              <Box role="status" aria-live="polite" aria-atomic="true">
                {t('details.featureDetailsTitle')
                  .replace('{count}', `${currentFeatureIndex + 1}`)
                  .replace('{total}', `${memoSelectedLayerDataFeatures?.length}`)}
              </Box>
            </Grid>
            {memoSelectedLayerData?.features && memoSelectedLayerData.features.length > 1 && (
              <Grid size={{ xs: 6 }} className="buttonGroup">
                {/* Navigation buttons use aria-disabled with manual styling to preserve keyboard focus when users reach first/last items */}
                <Box sx={{ textAlign: 'right' }}>
                  <IconButton
                    iconRef={prevButtonRef}
                    aria-label={t('details.previousFeatureBtn')}
                    tooltipPlacement="top"
                    onClick={() => {
                      if (!isPrevDisabled) {
                        handleFeatureNavigateChange(-1);
                      }
                    }}
                    aria-disabled={isPrevDisabled}
                    className="buttonOutline"
                  >
                    <ArrowBackIosOutlinedIcon />
                  </IconButton>
                  <IconButton
                    iconRef={nextButtonRef}
                    sx={{
                      marginLeft: '16px',
                    }}
                    aria-label={t('details.nextFeatureBtn')}
                    tooltipPlacement="top"
                    onClick={() => {
                      if (!isNextDisabled) {
                        handleFeatureNavigateChange(1);
                      }
                    }}
                    aria-disabled={isNextDisabled}
                    className="buttonOutline"
                  >
                    <ArrowForwardIosOutlinedIcon />
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>

          <FeatureInfo
            key={`${memoCurrentFeature.uid}-${memoCurrentFeatureHasGeometry ? 'with-geo' : 'no-geo'}`}
            feature={memoCurrentFeature}
            containerType={containerType}
          />
        </Box>
      );
    }

    // if no condition met, return null for Guide tab
    return null;
  };

  return (
    <Layout
      ref={layoutRef}
      containerType={containerType}
      titleFullscreen={t('details.title')}
      layoutSwitch={
        <Box sx={sxClasses.layoutSwitch}>
          {!hideCoordinateInfoSwitch && <CoordinateInfoSwitch disabled={!mapClickCoordinates} />}
          <IconButton
            aria-label={t('details.clearAllfeatures')}
            tooltipPlacement="top"
            onClick={() => handleClearAllHighlights()}
            className="buttonOutline"
            aria-disabled={checkedFeatures.length === 0}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', height: 24 }}>
              <ClearHighlightIcon sx={{ fontSize: 24 }} />
            </Box>
          </IconButton>
        </Box>
      }
      selectedLayerPath={selectedLayerPath}
      layerList={memoLayersList}
      onLayerListClicked={(layerEntry) => handleLayerChange(layerEntry)}
      onRightPanelClosed={handleRightPanelClosed}
      onRightPanelVisibilityChanged={handleRightPanelVisibilityChanged}
      guideContentIds={['details']}
      hideEnlargeBtn={containerType === CONTAINER_TYPE.APP_BAR}
      toggleMode={containerType === CONTAINER_TYPE.APP_BAR}
    >
      {renderContent()}
    </Layout>
  );
}
